import * as XLSX from 'xlsx';
import { Sale, MaintenanceRecord } from '../utils/types';
import { Debt } from '../utils/debtTypes';

// تصدير الفواتير إلى Excel
export const exportInvoicesToExcel = (sales: Sale[], fileName: string = 'invoices-report') => {
  try {
    // تحويل البيانات إلى تنسيق مناسب للتصدير
    const data = sales.map((sale, index) => ({
      '#': index + 1,
      'رقم الفاتورة': sale.id.substring(0, 8),
      'التاريخ': new Date(sale.createdAt).toLocaleDateString('ar-EG'),
      'العميل': sale.customerName || 'عميل عام',
      'الفرع': sale.branchName,
      'المخزن': sale.warehouseName,
      'المبلغ الأصلي': sale.total.toLocaleString(),
      'الخصم': sale.discount.toLocaleString(),
      'الإجمالي': sale.grandTotal.toLocaleString(),
      'طريقة الدفع': getPaymentMethodName(sale.paymentMethod),
      'حالة الدفع': getPaymentStatusName(sale.paymentStatus),
      'الموظف': sale.employeeName,
      'عدد الأصناف': sale.items.length,
      'ملاحظات': sale.notes || ''
    }));

    // إنشاء ورقة عمل
    const worksheet = XLSX.utils.json_to_sheet(data);

    // تحسين عرض الأعمدة
    const columnWidths = [
      { wch: 5 },   // #
      { wch: 15 },  // رقم الفاتورة
      { wch: 12 },  // التاريخ
      { wch: 20 },  // العميل
      { wch: 15 },  // الفرع
      { wch: 15 },  // المخزن
      { wch: 12 },  // المبلغ الأصلي
      { wch: 10 },  // الخصم
      { wch: 12 },  // الإجمالي
      { wch: 12 },  // طريقة الدفع
      { wch: 12 },  // حالة الدفع
      { wch: 20 },  // الموظف
      { wch: 12 },  // عدد الأصناف
      { wch: 30 }   // ملاحظات
    ];

    (worksheet as any)['!cols'] = columnWidths;

    // إنشاء مصنف عمل
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'الفواتير');

    // إضافة ملخص إحصائي
    const summaryData = [
      { 'البيان': 'إجمالي عدد الفواتير', 'القيمة': sales.length },
      { 'البيان': 'إجمالي المبيعات', 'القيمة': sales.reduce((sum, sale) => sum + sale.grandTotal, 0).toLocaleString() },
      { 'البيان': 'إجمالي الخصومات', 'القيمة': sales.reduce((sum, sale) => sum + sale.discount, 0).toLocaleString() },
      { 'البيان': 'متوسط قيمة الفاتورة', 'القيمة': sales.length > 0 ? (sales.reduce((sum, sale) => sum + sale.grandTotal, 0) / sales.length).toLocaleString() : '0' }
    ];

    const summaryWorksheet = XLSX.utils.json_to_sheet(summaryData);
    (summaryWorksheet as any)['!cols'] = [{ wch: 25 }, { wch: 20 }];
    XLSX.utils.book_append_sheet(workbook, summaryWorksheet, 'الملخص الإحصائي');

    // تصدير المصنف
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
    return true;
  } catch (error) {
    console.error('Error exporting invoices to Excel:', error);
    return false;
  }
};

// تصدير فاتورة واحدة إلى Excel مع تفاصيل كاملة
export const exportSingleInvoiceToExcel = (sale: Sale, fileName: string = `invoice-${sale.id.substring(0, 8)}`) => {
  try {
    // تفاصيل الفاتورة
    const invoiceDetails = [
      { 'البيان': 'رقم الفاتورة', 'القيمة': sale.id.substring(0, 8) },
      { 'البيان': 'التاريخ', 'القيمة': new Date(sale.createdAt).toLocaleDateString('ar-EG') },
      { 'البيان': 'العميل', 'القيمة': sale.customerName || 'عميل عام' },
      { 'البيان': 'الفرع', 'القيمة': sale.branchName },
      { 'البيان': 'المخزن', 'القيمة': sale.warehouseName },
      { 'البيان': 'الموظف', 'القيمة': sale.employeeName },
      { 'البيان': 'طريقة الدفع', 'القيمة': getPaymentMethodName(sale.paymentMethod) },
      { 'البيان': 'حالة الدفع', 'القيمة': getPaymentStatusName(sale.paymentStatus) },
      { 'البيان': '', 'القيمة': '' },
      { 'البيان': 'المبلغ الأصلي', 'القيمة': sale.total.toLocaleString() + ' ج.م' },
      { 'البيان': 'الخصم', 'القيمة': sale.discount.toLocaleString() + ' ج.م' },
      { 'البيان': 'الإجمالي النهائي', 'القيمة': sale.grandTotal.toLocaleString() + ' ج.م' },
      { 'البيان': 'ملاحظات', 'القيمة': sale.notes || 'لا توجد ملاحظات' }
    ];

    // أصناف الفاتورة
    const itemsData = sale.items.map((item, index) => ({
      '#': index + 1,
      'اسم الصنف': item.name,
      'الكمية': item.quantity,
      'السعر': item.price.toLocaleString(),
      'الإجمالي': item.total.toLocaleString(),
      'المخزن': item.warehouseName || '',
      'رمز الصنف': item.sku || ''
    }));

    // إنشاء أوراق العمل
    const detailsWorksheet = XLSX.utils.json_to_sheet(invoiceDetails);
    const itemsWorksheet = XLSX.utils.json_to_sheet(itemsData);

    // تحسين عرض الأعمدة
    (detailsWorksheet as any)['!cols'] = [{ wch: 20 }, { wch: 30 }];
    (itemsWorksheet as any)['!cols'] = [
      { wch: 5 },   // #
      { wch: 30 },  // اسم الصنف
      { wch: 10 },  // الكمية
      { wch: 12 },  // السعر
      { wch: 12 },  // الإجمالي
      { wch: 15 },  // المخزن
      { wch: 15 }   // رمز الصنف
    ];

    // إنشاء مصنف عمل
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, detailsWorksheet, 'تفاصيل الفاتورة');
    XLSX.utils.book_append_sheet(workbook, itemsWorksheet, 'الأصناف');

    // تصدير المصنف
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
    return true;
  } catch (error) {
    console.error('Error exporting single invoice to Excel:', error);
    return false;
  }
};

// تصدير فواتير الصيانة إلى Excel
export const exportMaintenanceInvoicesToExcel = (records: MaintenanceRecord[], fileName: string = 'maintenance-invoices') => {
  try {
    const data = records.map((record, index) => ({
      '#': index + 1,
      'رقم الطلب': record.orderNumber || record.id.substring(0, 8),
      'التاريخ': new Date(record.receivedAt).toLocaleDateString('ar-EG'),
      'العميل': record.customerName,
      'نوع الجهاز': record.deviceType,
      'المشكلة': record.issue,
      'التشخيص': record.diagnosis || '',
      'الحالة': getMaintenanceStatusName(record.status),
      'التكلفة الأولية': record.initialCost?.toLocaleString() || '0',
      'التكلفة النهائية': record.finalCost?.toLocaleString() || '0',
      'المدفوع': record.paid?.toLocaleString() || '0',
      'المتبقي': record.remainingAmount?.toLocaleString() || '0',
      'الفرع': record.branchName,
      'المخزن': record.warehouseName,
      'ملاحظات': record.notes || ''
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    
    const columnWidths = [
      { wch: 5 },   // #
      { wch: 15 },  // رقم الطلب
      { wch: 12 },  // التاريخ
      { wch: 20 },  // العميل
      { wch: 15 },  // الهاتف
      { wch: 15 },  // نوع الجهاز
      { wch: 15 },  // موديل الجهاز
      { wch: 30 },  // المشكلة
      { wch: 12 },  // الحالة
      { wch: 12 },  // التكلفة
      { wch: 20 },  // الفني
      { wch: 15 },  // الفرع
      { wch: 30 }   // ملاحظات
    ];

    (worksheet as any)['!cols'] = columnWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'فواتير الصيانة');

    XLSX.writeFile(workbook, `${fileName}.xlsx`);
    return true;
  } catch (error) {
    console.error('Error exporting maintenance invoices to Excel:', error);
    return false;
  }
};

// دوال مساعدة
const getPaymentMethodName = (method: string): string => {
  const methods: { [key: string]: string } = {
    'cash': 'نقدي',
    'card': 'بطاقة',
    'bank_transfer': 'تحويل بنكي',
    'debt': 'دين'
  };
  return methods[method] || method;
};

const getPaymentStatusName = (status: string): string => {
  const statuses: { [key: string]: string } = {
    'paid': 'مدفوع',
    'pending': 'معلق',
    'partial': 'دفع جزئي',
    'debt': 'دين'
  };
  return statuses[status] || status;
};

const getMaintenanceStatusName = (status: string): string => {
  const statuses: { [key: string]: string } = {
    'pending': 'في الانتظار',
    'in_progress': 'قيد التنفيذ',
    'completed': 'مكتمل',
    'delivered': 'تم التسليم',
    'cancelled': 'ملغي',
    'refunded': 'مرتجع'
  };
  return statuses[status] || status;
};
